<template>
  <div class="w-full overflow-x-hidden">
    <page-loader v-if="loading" message="Loading Job details..." />
    <Page v-else>
      <div class="page">
        <JobHeader @refresh-job="refreshJob" />
        <TabSwitcher id="jp" :tabs="tabs" @tab-change="onTabChange" />
        <ImportedDataWarning />
        <JobStatusFinalWarning />
      </div>
    </Page>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { getProperty } from "@/lib/helpers.js";
import { getPathValueAt, updateQueryParam } from "@/lib/url.js";
import { useAccessRole } from "@/lib/useAccessRole";

import ImportedDataWarning from "@/components/ImportedDataWarning.vue";
import JobStatusFinalWarning from "@/components/JobStatusFinalWarning.vue";
import Page from "@/components/Page.vue";
import PageLoader from "@/components/PageLoader.vue";
import TabSwitcher from "@/components/TabSwitcher/TabSwitcher.vue";
import useVuelidate from "@vuelidate/core";
import JobHeader from "./Header/JobHeader.vue";
import AppointmentTab from "./Tabs/AppointmentTab.vue";
import InvoiceTab from "./Tabs/InvoiceTab.vue";
import JobCallsTab from "./Tabs/JobCallsTab.vue";
import JobTimesTab from "./Tabs/JobTimesTab.vue";
import PhotoDocTab from "./Tabs/PhotoDocTab.vue";
import PurchaseOrdersTab from "./Tabs/PurchaseOrdersTab.vue";
import QuotesTab from "./Tabs/QuotesTab.vue";

export default {
  name: "JobPage",
  components: {
    Page,
    JobHeader,
    TabSwitcher,
    ImportedDataWarning,
    JobStatusFinalWarning,
    PageLoader
  },
  setup() {
    const { canAccess } = useAccessRole();
    return {
      canAccess,
      v$: useVuelidate()
    };
  },
  data() {
    return {
      loading: true
    };
  },
  computed: {
    ...mapState({
      tenant: "tenant",
      fetchingTenant: "fetchingTenant",
      currentUser: "currentUser",
      job: "job",
      tabs({ tenant }) {
        const tabs = [
          { name: "Appointments", component: AppointmentTab },
          {
            name: "Quotes",
            ref: "quotesTab",
            component: QuotesTab,
            beforeTabChange: (ref) => {
              updateQueryParam("quote_id", null);
              ref?.setCurrentQuote(null);
              return true;
            },
            visible: this.canAccess("quoting")
          },
          {
            name: "Invoice",
            component: InvoiceTab,
            visible: this.canAccess("financial_data")
          },
          { name: "Photos and Documents", component: PhotoDocTab },
          { name: "Purchase Orders", component: PurchaseOrdersTab },
          { name: "Job Times", component: JobTimesTab },
          {
            name: "Phone Calls",
            component: JobCallsTab
          }
        ];
        return tabs;
      }
    })
  },
  watch: {
    job(newValue, oldValue) {
      if (
        getProperty(newValue, "customer.id") !==
        getProperty(oldValue, "customer.id")
      ) {
        this.setCurrentUser(newValue.customer);
        this.$store.dispatch("addBreadcrumb", { label: `Job #${newValue.id}` });
      }
    }
  },
  async created() {
    await import("./jobs_tailwind.css");
  },
  async mounted() {
    EventBus.$on("refresh-job", this.refreshJob);
    try {
      const jobId = getPathValueAt(1);
      await Promise.all([
        this.fetchJob({ jobId }),
        this.fetchTenantCustomizations()
      ]);
      if (!this.tenant && !this.fetchingTenant) {
        await this.fetchTenant();
      }
    } catch (e) {
      console.log(e);
      // Do nothing for now
    }
    this.loading = false;
  },
  beforeUnmount() {
    EventBus.$off("refresh-job", this.refreshJob);
    this.clearCurrentUser();
    this.clearJob();
  },
  methods: {
    ...mapActions([
      "fetchTenantCustomizations",
      "fetchJob",
      "setCurrentUser",
      "fetchTenant",
      "clearCurrentUser",
      "clearJob"
    ]),
    refreshJob() {
      if (this.job?.id) {
        this.fetchJob({ jobId: this.job.id });
      }
    },
    onTabChange(tabName) {
      // Clear query params
      if (tabName !== "Appointments") {
        updateQueryParam("appointment_id", null);
      }
      if (tabName !== "Quotes") {
        updateQueryParam("quote_id", null);
      }
    }
  }
};
</script>

<style scoped>
.page > div {
  margin-bottom: 1rem;
}
</style>
